import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import {
  Box,
  Button,
  Chip,
  Divider,
  Stack,
  Typography,
  type SxProps,
  type Theme,
} from "@mui/material";
import type { FC, ReactNode } from "react";

const sectionSx: SxProps<Theme> = {
  border: "1px solid",
  borderColor: "divider",
  borderRadius: 2.5,
  bgcolor: "background.paper",
  p: { xs: 2.25, sm: 2.75 },
};

export const CardInfoHeader: FC<{
  title: string;
  subtitle?: ReactNode;
  onEdit?: () => void;
  editLabel?: string;
  extra?: ReactNode;
}> = ({ title, subtitle, onEdit, editLabel = "עריכה", extra }) => (
  <Stack
    direction="row"
    alignItems="flex-start"
    justifyContent="space-between"
    gap={2}
    sx={{ mb: 0.5 }}
  >
    <Box sx={{ minWidth: 0, flex: 1 }}>
      <Typography
        variant="h5"
        fontWeight={700}
        sx={{
          lineHeight: 1.25,
          wordBreak: "break-word",
          fontSize: { xs: "1.35rem", sm: "1.5rem" },
        }}
      >
        {title}
      </Typography>
      {subtitle ? (
        <Box
          sx={{
            mt: 1,
            color: "text.secondary",
            typography: "body1",
            fontSize: "1.05rem",
            lineHeight: 1.5,
            "& .MuiTypography-root": {
              fontSize: "inherit",
              lineHeight: "inherit",
            },
          }}
        >
          {subtitle}
        </Box>
      ) : null}
      {extra}
    </Box>
    {onEdit ? (
      <Button
        variant="outlined"
        size="medium"
        startIcon={<EditOutlinedIcon />}
        onClick={onEdit}
        sx={{
          flexShrink: 0,
          borderRadius: 2,
          textTransform: "none",
          fontWeight: 600,
          px: 2,
          py: 0.75,
          fontSize: "0.95rem",
        }}
      >
        {editLabel}
      </Button>
    ) : null}
  </Stack>
);

export const CardInfoSection: FC<{
  title?: string;
  children: ReactNode;
  sx?: SxProps<Theme>;
}> = ({ title, children, sx }) => (
  <Box sx={[sectionSx, ...(Array.isArray(sx) ? sx : sx ? [sx] : [])] as SxProps<Theme>}>
    {title ? (
      <>
        <Typography
          variant="subtitle1"
          fontWeight={700}
          color="text.secondary"
          sx={{ mb: 1.75, letterSpacing: 0.15, fontSize: "1.05rem" }}
        >
          {title}
        </Typography>
        <Divider sx={{ mb: 2 }} />
      </>
    ) : null}
    {children}
  </Box>
);

export const CardInfoGrid: FC<{ children: ReactNode }> = ({ children }) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: {
        xs: "1fr",
        sm: "repeat(2, minmax(0, 1fr))",
      },
      gap: { xs: 2, sm: 2.25 },
      rowGap: { xs: 2.25, sm: 2.5 },
    }}
  >
    {children}
  </Box>
);

export const CardInfoField: FC<{
  label: string;
  children: ReactNode;
  fullWidth?: boolean;
}> = ({ label, children, fullWidth }) => (
  <Box
    sx={{
      minWidth: 0,
      gridColumn: fullWidth ? "1 / -1" : undefined,
    }}
  >
    <Typography
      variant="body2"
      color="text.secondary"
      sx={{ display: "block", mb: 0.6, fontWeight: 700, fontSize: "0.95rem" }}
    >
      {label}
    </Typography>
    <Box
      sx={{
        typography: "body1",
        color: "text.primary",
        fontSize: "1.125rem",
        fontWeight: 500,
        wordBreak: "break-word",
        whiteSpace: "pre-wrap",
        lineHeight: 1.55,
        minHeight: "1.55em",
        "& .MuiTypography-root": {
          fontSize: "inherit",
          fontWeight: "inherit",
          lineHeight: "inherit",
        },
      }}
    >
      {children == null || children === "" ? "-" : children}
    </Box>
  </Box>
);

export const CardInfoChips: FC<{ items: string[] }> = ({ items }) => {
  if (!items.length) {
    return (
      <Typography
        color="text.secondary"
        sx={{ fontSize: "1.125rem", fontWeight: 500 }}
      >
        -
      </Typography>
    );
  }
  return (
    <Stack direction="row" flexWrap="wrap" gap={1}>
      {items.map((item) => (
        <Chip
          key={item}
          size="medium"
          label={item}
          sx={{
            borderRadius: 2,
            height: "auto",
            py: 0.6,
            fontSize: "0.95rem",
            fontWeight: 600,
            "& .MuiChip-label": {
              px: 1.25,
              whiteSpace: "normal",
            },
          }}
        />
      ))}
    </Stack>
  );
};

export const CardInfoView: FC<{ children: ReactNode }> = ({ children }) => (
  <Stack spacing={2.5} sx={{ mt: 1.25, width: "100%" }}>
    {children}
  </Stack>
);
